import { useEffect, useState } from "react";
import avatarPlaceholder from "../../../../assets/images/default_avatar.jpg";
import { ExpiryAlertInterface } from "./types";
import { DataTable } from "../../../DataTable/DataTable";
import { ChevronLeft } from "lucide-react";
import toast from "react-hot-toast";
import { InverntroyService } from "../../../../services/api/inventroy.service";
import * as XLSX from "xlsx";

const ExpiryAlert = ({
  setShowexpiryAlert,
  clinicId,
}: {
  setShowexpiryAlert?: React.Dispatch<React.SetStateAction<boolean>>;
  clinicId?: string;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [expireIn, setExpireIn] = useState("90");
  const [expiryDetails, setExpiryDetails] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const handleLoadExpiryDetails = async (
    currentPage: number,
    pageSize: number,
    expireIn: string,
    completeData: boolean
  ) => {
    try {
      const response = await InverntroyService.getExpiryAlerts({
        page: currentPage,
        pageSize,
        expireIn,
        clinicId,
        completeData,
      });
      if (response.success) {
        setExpiryDetails(response.data);
      } else {
        toast.error(response.message);
        setExpiryDetails([]);
      }
      setTotalItemsCount(response.pagination.totalItemsCount || 0);
      setTotalPages(response.pagination.totalPages || 1);
    } catch (error) {
      console.log(error);
      toast.error("Error while fetching the expiry details");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    handleLoadExpiryDetails(currentPage, pageSize, expireIn, false);
  }, [currentPage, pageSize, expireIn]);
  const columns = [
    {
      key: "Product Name",
      title: "Product Name",
      sortable: true,
      width: "w-[250px]",
      render: (expire: ExpiryAlertInterface) => (
        <div className="flex items-center">
          <img
            src={expire?.product?.productImage || avatarPlaceholder}
            alt={expire?.product?.name}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900">
              {expire?.product?.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "Clinic Name",
      title: "Clinic Name",
      sortable: true,
      width: "w-[250px]",
      render: (expire: ExpiryAlertInterface) => (
        <div className="flex items-center">
          <img
            src={expire?.clinic?.profileImage || avatarPlaceholder}
            alt={expire?.clinic?.name}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900">
              {expire?.clinic?.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "Pricing",
      title: "Pricing",
      sortable: true,
      render: (expire: ExpiryAlertInterface) => (
        <>
          <div>{Number(expire?.product_rate || 0).toFixed(2) || "N/A"}</div>
        </>
      ),
    },
    {
      key: "Expiry Date",
      title: "Expiry Date",
      render: (expire: ExpiryAlertInterface) => {
        if (!expire?.expiry_date) return <div>N/A</div>;

        const date = new Date(expire.expiry_date);
        const formatted = date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });

        return <div>{formatted}</div>;
      },
    },
    {
      key: "Quantity",
      title: "Quantity",
      render: (expire: ExpiryAlertInterface) => (
        <div>
          {(expire?.no_of_items || 0) + (expire?.free_items || 0) || "N/A"}
        </div>
      ),
    },
    {
      key: "Manufacturer",
      title: "Wholesaler",
      render: (expire: ExpiryAlertInterface) => (
        <div>{expire?.wholesaler?.name || "N/A"}</div>
      ),
    },
    {
      key: "Alert",
      title: "Alert",
      render: (expire: ExpiryAlertInterface) => {
        const expiredText = expire.expiredIn || "N/A";
        const daysMatch = expiredText.match(/(\d+)\s+days?/i);
        console.log("\n\n daysMatch", daysMatch, "");
        const days = daysMatch ? parseInt(daysMatch[1], 10) : null;

        let alertClasses =
          "flex items-center px-3 py-2 rounded-full text-sm font-medium w-fit";
        let dotClasses = "w-2 h-2 rounded-full mr-2";

        if (days !== null) {
          if (days > 90) {
            alertClasses += " bg-green-50 text-green-600";
            dotClasses += " bg-green-500";
          } else if (days > 30) {
            alertClasses += " bg-orange-50 text-orange-600";
            dotClasses += " bg-orange-500";
          } else {
            alertClasses += " bg-red-50 text-red-600";
            dotClasses += " bg-red-500";
          }
        } else {
          alertClasses += " bg-gray-50 text-gray-600";
          dotClasses += " bg-gray-500";
        }

        return (
          <div className={alertClasses}>
            <div className={dotClasses}></div>
            <span>{expiredText}</span>
          </div>
        );
      },
    },
  ];

  const handleExportToExcel = async () => {
    setDownloading(true);
    try{
    const completeData=true;
    const response = await InverntroyService.getExpiryAlerts({
        page: currentPage,
        pageSize,
        expireIn,
        clinicId,
        completeData,
      });

      if(response.data.length === 0){
        toast.error("No data found");
        return;
      }

    // Convert the API data into a clean Excel-friendly structure
    const formattedData = response.data.map((item: any) => {
      let expiryFormatted = "N/A";

      if (item.expiry_date) {
        const date = new Date(item.expiry_date);
        expiryFormatted = date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      }

      return {
        "Product Name": item?.product?.name || "N/A",
        "Clinic Name": item?.clinic?.name || "N/A",
        Price: Number(item?.product_rate || 0).toFixed(2) || "N/A",
        "Expiry Date": expiryFormatted,
        "Total Quantity": item.no_of_items + item.free_items || "N/A",
        Wholesaler: item?.wholesaler?.name || "N/A",
        "Invoice Number": item?.invoice_number || "N/A",
        "Invoice Date": item?.invoice_date
  ? new Date(item.invoice_date).toLocaleDateString("en-US")
  : "N/A",

        Alerts: item?.expiredIn || "N/A",
      };
    });

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expiry Alerts");

    // Generate Excel File
    XLSX.writeFile(workbook, `expiry_alerts.xlsx`);
  }catch(error){
    console.log(error);
    toast.error("Error while exporting the expiry details");
  }finally{
    setDownloading(false);
  }
}
  return (
    <>
      {!clinicId && (<button
        className="text-sm mb-4 flex"
        onClick={() => setShowexpiryAlert(false)}
      >
        <ChevronLeft className="w-5 h-5 mr-2 cursor-pointer bg-gray-200 rounded-full" />{" "}
        Back
      </button>)}

      <div className="flex items-end justify-end">
        <button
          onClick={handleExportToExcel}
          className="mb-4 px-4 py-2 left-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
          disabled={downloading}
        >
          {downloading ? "Exporting..." : "Export to Excel"}
        </button>
      </div>
      <DataTable
        data={expiryDetails}
        columns={columns}
        itemsPerPage={pageSize}
        isLoading={isLoading}
        pagination={{
          currentPage,
          setCurrentPage,
          setTotalPages,
          totalPages,
          totalItemsCount,
          pageSize,
          setPageSize,
        }}
      />
    </>
  );
};

export default ExpiryAlert;
